/**
 * E-IMZO (ERI) — brauzerdan imzolash (CAPIWS).
 *
 * Foydalanuvchi kompyuterida ishlayotgan E-IMZO dasturi `wss://127.0.0.1:64443`
 * (zaxira: `ws://127.0.0.1:64646`) orqali gaplashadi. Maxfiy kalit hech qachon
 * brauzerga yoki serverga chiqmaydi: dastur kalit parolini o'zi so'raydi va faqat
 * tayyor PKCS#7 (Base64) imzoni qaytaradi. Backend shu imzoni kalit markazi orqali
 * tekshiradi (`EImzoAdapter.verify_signature`).
 *
 * Domen kaliti (`VITE_EIMZO_API_KEYS`, «domen,kalit;domen,kalit» ko'rinishida)
 * E-IMZO bergan bo'lishi shart — u yo'q bo'lsa dastur imzoga ruxsat bermaydi.
 * `signSandbox` faqat qarorlarni imzolash oynasida (`EriDecisionDialog`) sinov uchun ishlatiladi;
 * kabinetga kirishda sinov rejimi YO'Q — faqat haqiqiy ERI kaliti qabul qilinadi.
 */

export interface EriCertificate {
  disk: string;
  path: string;
  name: string;
  alias: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
  CN: string;
  TIN: string;
  PINFL: string;
  O: string;
  T: string;
}

const ENDPOINTS = ["wss://127.0.0.1:64443/service/cryptapi", "ws://127.0.0.1:64646/service/cryptapi"];
const CONNECT_TIMEOUT_MS = 2500;

interface CapiResponse {
  success: boolean;
  reason?: string;
  [key: string]: unknown;
}

let socket: WebSocket | null = null;
let queue: Promise<unknown> = Promise.resolve();

function open(url: string): Promise<WebSocket> {
  return new Promise((resolve, reject) => {
    let done = false;
    const ws = new WebSocket(url);
    const timer = window.setTimeout(() => {
      if (done) return;
      done = true;
      ws.close();
      reject(new Error("timeout"));
    }, CONNECT_TIMEOUT_MS);
    ws.onopen = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      resolve(ws);
    };
    ws.onerror = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      reject(new Error("connect"));
    };
  });
}

async function connect(): Promise<WebSocket> {
  if (socket && socket.readyState === WebSocket.OPEN) return socket;
  socket = null;
  for (const url of ENDPOINTS) {
    try {
      socket = await open(url);
      socket.onclose = () => {
        socket = null;
      };
      await installKeys(socket);
      return socket;
    } catch {
      socket = null;
    }
  }
  throw new Error("E-IMZO dasturi topilmadi.");
}

/** Bir vaqtda faqat bitta so'rov: dastur javoblarni tartib bilan qaytaradi. */
function call(ws: WebSocket, request: Record<string, unknown>): Promise<CapiResponse> {
  const run = () =>
    new Promise<CapiResponse>((resolve, reject) => {
      const onMessage = (event: MessageEvent) => {
        ws.removeEventListener("message", onMessage);
        try {
          resolve(JSON.parse(String(event.data)) as CapiResponse);
        } catch {
          reject(new Error("E-IMZO javobini o'qib bo'lmadi."));
        }
      };
      ws.addEventListener("message", onMessage);
      ws.send(JSON.stringify(request));
    });
  const next = queue.then(run, run);
  queue = next.catch(() => undefined);
  return next;
}

async function installKeys(ws: WebSocket): Promise<void> {
  const raw = (import.meta.env.VITE_EIMZO_API_KEYS as string | undefined) ?? "";
  const keys = raw
    .split(";")
    .flatMap((pair) => pair.split(",").map((part) => part.trim()))
    .filter(Boolean);
  if (keys.length === 0) return;
  await call(ws, { name: "apikey", arguments: keys });
}

/** E-IMZO dasturi ulanadimi (interfeys shunga qarab sertifikat ro'yxatini ko'rsatadi). */
export async function eriAvailable(): Promise<boolean> {
  try {
    await connect();
    return true;
  } catch {
    return false;
  }
}

export async function listCertificates(): Promise<EriCertificate[]> {
  const ws = await connect();
  const response = await call(ws, { plugin: "certkey", name: "list_all_certificates" });
  if (!response.success) throw new Error(String(response.reason ?? "Sertifikatlar o'qilmadi."));
  const items = (response.certificates as EriCertificate[] | undefined) ?? [];
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, ".");
  /* Muddati o'tgan sertifikat bilan imzolab bo'lmaydi (E4) — ro'yxatdan chiqariladi. */
  return items.filter((item) => !item.validTo || item.validTo.slice(0, 10).replace(/-/g, ".") >= today);
}

function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

/** Foydalanuvchi parol oynasini yopgan bo'lsa (bekor qilish) — boshqa usul bilan qayta urinilmaydi. */
const CANCELLED = /cancel|bekor|otmen|отмен|abort/i;

/**
 * Kalitni E-IMZO dasturiga yuklaydi (parol oynasi shu dasturda chiqadi). Ro'yxat `certkey`
 * plaginidan olingani uchun avval shu plagin; u kalitni tanimasa — eski `pfx` plagini.
 */
async function loadKey(ws: WebSocket, cert: EriCertificate): Promise<CapiResponse> {
  const args = [cert.disk, cert.path, cert.name, cert.alias];
  const first = await call(ws, { plugin: "certkey", name: "load_key", arguments: args });
  if (first.success || CANCELLED.test(String(first.reason ?? ""))) return first;
  const second = await call(ws, { plugin: "pfx", name: "load_key", arguments: args });
  return second.success ? second : first;
}

/** Yuklangan kalit (`keyId`) bilan matnni imzolaydi — PKCS#7 (Base64). */
async function createPkcs7(ws: WebSocket, keyId: unknown, challenge: string): Promise<string> {
  const signed = await call(ws, {
    plugin: "pkcs7",
    name: "create_pkcs7",
    arguments: [toBase64(challenge), keyId, "no"],
  });
  if (!signed.success) throw new Error(String(signed.reason ?? "Imzolash bekor qilindi."));
  return String(signed.pkcs7_64);
}

/** Qaror matnini (challenge) tanlangan sertifikat bilan imzolaydi — PKCS#7 (Base64). */
export async function signChallenge(cert: EriCertificate, challenge: string): Promise<string> {
  const ws = await connect();
  const loaded = await loadKey(ws, cert);
  if (!loaded.success) throw new Error(String(loaded.reason ?? "Kalitni ochib bo'lmadi."));
  return createPkcs7(ws, loaded.keyId, challenge);
}

/* ------------------------------------------------------------ kirish uchun kalitlar */

/** Kabinetga kirishda tanlanadigan ERI kaliti (E-IMZO dasturi ko'rsatgan sertifikat). */
export interface EriKey {
  /** Ro'yxatda takrorlanmas identifikator. */
  id: string;
  /** Egasi (F.I.Sh.). */
  title: string;
  /** Tashkilot / lavozim. */
  subtitle: string;
  serial: string;
  /** Amal qilish muddati (YYYY-MM-DD) yoki bo'sh. */
  validTo: string;
  /** ЖШШИР oxirgi 4 raqami (maxfiylik uchun maskalangan) yoki bo'sh. */
  pinflTail: string;
  cert: EriCertificate;
}

function isoDate(value: string | undefined): string {
  const match = /(\d{4})[.\-/](\d{2})[.\-/](\d{2})/.exec(value ?? "");
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
}

function pinflTail(pinfl: string | undefined): string {
  const digits = (pinfl ?? "").replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : "";
}

/** E-IMZO dasturidagi (muddati o'tmagan) ERI kalitlari ro'yxati. */
export async function listKeys(): Promise<EriKey[]> {
  const certs = await listCertificates();
  return certs.map((cert) => ({
    id: `${cert.disk}:${cert.path}:${cert.alias}`,
    title: cert.CN || cert.name,
    subtitle: [cert.O, cert.T].filter(Boolean).join(" · "),
    serial: cert.serialNumber,
    validTo: isoDate(cert.validTo),
    pinflTail: pinflTail(cert.PINFL),
    cert,
  }));
}

/** Kirish matnini (challenge) tanlangan ERI kaliti bilan imzolaydi — PKCS#7 (Base64). */
export function signWithKey(key: EriKey, challenge: string): Promise<string> {
  return signChallenge(key.cert, challenge);
}

/** Sinov rejimi tugmasi ko'rsatiladimi (ishlab chiqish yoki sandbox muhiti). */
export const ERI_SANDBOX_ALLOWED =
  import.meta.env.DEV || import.meta.env.VITE_ERI_SANDBOX === "1";

/**
 * Sinov (sandbox) imzosi — kalit markazi ulanmagan muhit uchun. Haqiqiy imzo emas:
 * faqat backend sandbox rejimida qabul qilinadi va qarorda «sandbox» deb belgilanadi.
 */
export async function signSandbox(serial: string, challenge: string): Promise<string> {
  const data = new TextEncoder().encode(`${serial}|${challenge}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const hex = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  return `sandbox-eri:${serial}:${hex}`;
}
